// src/pages/ProfilePage.js - FINAL VERSION (SMART URL FIX)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkIcon from '@mui/icons-material/Work';
import LanguageIcon from '@mui/icons-material/Language';
import { useAuth } from '../context/AuthContext';
import userService from '../services/userService';
import { getPasswordPolicyError, PASSWORD_POLICY_HELPER } from '../utils/passwordPolicy';

// ✅ HELPER "INTELIGENTE": Construye la ruta completa
const getAvatarUrl = (path) => {
    if (!path) return undefined;
    
    // 1. Si ya es una URL web completa (ej: https://...), la dejamos tal cual
    if (path.startsWith('http')) return path;
    
    // 2. Definir la URL base del backend a partir del entorno activo
    const rawApiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
    const baseUrl = rawApiUrl.replace(/\/api$/i, '') || window.location.origin;

    let cleanPath = path;

    // Asegurar que empiece con /
    if (!cleanPath.startsWith('/')) {
        cleanPath = `/${cleanPath}`;
    }

    // 🚨 CORRECCIÓN CRÍTICA:
    // Si la BD guardó solo "avatar-123.jpg" y falta la carpeta, la agregamos.
    // Esto es lo que faltaba para que Nginx detecte la ruta "/uploads/"
    if (!cleanPath.includes('/uploads')) {
        cleanPath = `/uploads/avatars${cleanPath}`;
    }

    // 3. Retornar URL final + Timestamp para evitar caché viejo
    return `${baseUrl}${cleanPath}?t=${new Date().getTime()}`;
};

export default function ProfilePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, updateUser } = useAuth(); // Usamos updateUser del contexto

  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    department: 'Technology',
    position: 'Developer',
    location: 'Mexico City, Mexico',
    bio: '',
    website: '',
    avatar: ''
  });

  const [stats, setStats] = useState({
    projects: 0,
    tasksCompleted: 0,
    hoursWorked: 0,
    collaborations: 0
  });

  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  // Cargar datos del usuario
  useEffect(() => {
    if (user) {
      setProfileData({
        username: user.username || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        avatar: user.avatar || '', // Se actualiza automáticamente
        department: user.department || 'Technology',
        position: user.job_title || 'Developer',
        location: user.address || 'Mexico City, Mexico',
        bio: user.bio || '',
        website: user.website || '',
      });
    }
  }, [user]);

  // Cargar estadísticas
  useEffect(() => {
    const loadStats = async () => {
      if (user?.id) {
        try {
          const data = await userService.getUserStats(user.id);
          setStats(data.stats);
        } catch (error) {
          console.error('Error loading stats:', error);
          setStats({ projects: 12, tasksCompleted: 48, hoursWorked: 320, collaborations: 24 });
        }
      }
    };
    loadStats();
  }, [user]);

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await userService.updateMyProfile({
        email: profileData.email,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        phone: profileData.phone,
        job_title: profileData.position,
        address: profileData.location,
        bio: profileData.bio,
      });

      setMessage('✅ Profile updated successfully');
      
      const updatedUser = { 
        ...user, 
        email: profileData.email,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        phone: profileData.phone,
        job_title: profileData.position,
        address: profileData.location,
        bio: profileData.bio,
      };
      
      if (updateUser) {
          updateUser(updatedUser);
      } else {
          localStorage.setItem('user', JSON.stringify(updatedUser)); 
          window.location.reload();
      }
      
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('❌ Error updating profile: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setError('');
    setMessage('');

    if (!passwords.current || !passwords.new || !passwords.confirm) {
      setError('❌ All fields are required');
      return;
    }
    if (passwords.new !== passwords.confirm) {
      setError('❌ Passwords do not match');
      return;
    }
    const passwordPolicyError = getPasswordPolicyError(passwords.new, user);
    if (passwordPolicyError) {
      setError(`Password policy: ${passwordPolicyError}`);
      return;
    }

    setLoading(true);

    try {
      await userService.updateMyProfile({
        password: passwords.new,
        current_password: passwords.current
      });

      setMessage('✅ Password updated successfully');
      setOpenPasswordDialog(false);
      setPasswords({ current: '', new: '', confirm: '' });
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('Error updating password: ' + (err.response?.data?.message || err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ✅ SUBIDA DE IMAGEN
  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('❌ Please select a valid image');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('❌ Image must be smaller than 2MB');
      return;
    }

    setUploadingAvatar(true);
    setError('');

    try {
      const response = await userService.uploadMyAvatar(file);
      
      setMessage('✅ Profile picture updated');
      
      const avatarUrl = response.avatar; 

      if (updateUser) {
          updateUser({ ...user, avatar: avatarUrl });
      } else {
          const updatedUser = { ...user, avatar: avatarUrl };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          window.location.reload();
      }
      
      setTimeout(() => setMessage(''), 3000);
    } catch (uploadError) {
      console.error(uploadError);
      setError('❌ Error uploading photo: ' + (uploadError.response?.data?.message || uploadError.message));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const statsData = [
    { label: 'Projects', value: stats.projects, color: '#1976d2' },
    { label: 'Tasks Completed', value: stats.tasksCompleted, color: '#2e7d32' },
    { label: 'Hours Worked', value: stats.hoursWorked, color: '#ed6c02' },
    { label: 'Collaborations', value: stats.collaborations, color: '#9c27b0' },
  ];

  const activities = [
    { text: 'Completed task "UI Design"', date: '2 hours ago' },
    { text: 'Uploaded 3 files to Alpha project', date: '5 hours ago' },
    { text: 'Commented on Beta project', date: 'Yesterday' },
    { text: 'Joined the development team', date: '3 days ago' },
  ];

  const fullName = `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || profileData.username || 'User';

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Alerts */}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Header with Avatar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.35, md: 1.6 },
          mb: 1.5,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 1.4 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={getAvatarUrl(profileData.avatar)} // ✅ Usa la función inteligente
              sx={{
                width: 66,
                height: 66,
                fontSize: '1.7rem',
                border: '2px solid white',
                bgcolor: profileData.avatar ? 'transparent' : 'rgba(255,255,255,0.2)',
              }}
            >
              {!profileData.avatar && fullName.split(' ').map((n) => n[0]).join('').toUpperCase()}
            </Avatar>
            <IconButton
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                bgcolor: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                boxShadow: 2,
              }}
              component="label"
              disabled={uploadingAvatar}
            >
              {uploadingAvatar ? <CircularProgress size={24} sx={{ color: 'primary.main' }} /> : <PhotoCameraIcon sx={{ color: 'primary.main' }} />}
              <input type="file" hidden accept="image/*" onChange={handleAvatarChange} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' }, mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{fullName}</Typography>
              <Chip
                icon={<VerifiedUserIcon />}
                label={user.role === 'admin' ? 'Administrator' : user.role === 'manager' ? 'Manager' : 'User'}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid white' }}
              />
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 0.5 }}>
              {profileData.position} • {profileData.department}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              Member since {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A'}
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<LockIcon />}
            onClick={() => setOpenPasswordDialog(true)}
            size="small"
            sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
          >
            Change Password
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={1.2}>
        {/* Stats */}
        <Grid item xs={12}>
          <Grid container spacing={1.2}>
            {statsData.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <CardContent sx={{ textAlign: 'center', py: 1.2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: stat.color }}>
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Personal Information</Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                  InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Username" value={profileData.username} disabled helperText="Username cannot be changed" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Department"
                  value={profileData.department}
                  onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                  InputProps={{ startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Position"
                  value={profileData.position}
                  onChange={(e) => setProfileData({ ...profileData, position: e.target.value })}
                  InputProps={{ startAdornment: <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={profileData.location}
                  onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                  InputProps={{ startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website"
                  value={profileData.website}
                  onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
                  InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Bio"
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  placeholder="Tell us about yourself..."
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveProfile}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} lg={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Recent Activity</Typography>
            <List sx={{ p: 0 }}>
              {activities.map((activity, index) => (
                <React.Fragment key={index}>
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <PersonIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{activity.text}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{activity.date}</Typography>}
                    />
                  </ListItem>
                  {index < activities.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog - Change Password */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon color="primary" />
            Change Password
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                helperText={PASSWORD_POLICY_HELPER}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                error={passwords.new !== '' && passwords.confirm !== '' && passwords.new !== passwords.confirm}
                helperText={passwords.new !== '' && passwords.confirm !== '' && passwords.new !== passwords.confirm ? 'Passwords do not match' : ''}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={loading || !passwords.current || !passwords.new || passwords.new !== passwords.confirm}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
